import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";
import { AccountApiError } from "../lib/account-api";
import { loadFavoriteTickers, loadSession, saveFavoriteTicker } from "../lib/favorites-api";
import { applySavedView, captureSavedViewState } from "../lib/screener-state";
import {
  createSavedView as createSavedViewRecord,
  deleteSavedView as deleteSavedViewRecord,
  loadSavedViews,
  normalizeSavedViewName,
  updateSavedView as updateSavedViewRecord,
  type SavedViewRecord,
  type SavedViewUpdate
} from "../lib/saved-views-api";
import type { ScreenerState, UserSession } from "../lib/types";

export type AccountReadState =
  | { availability: "loading" }
  | { availability: "ready" }
  | {
      availability: "error";
      kind: "session" | "favorites" | "saved-views";
      error: Error;
    };

interface AccountSnapshot {
  session: UserSession;
  favoriteTickers: string[];
  savedViews: SavedViewRecord[];
}

type AccountLoadOutcome =
  | { ok: true; snapshot: AccountSnapshot }
  | {
      ok: false;
      kind: "session" | "favorites" | "saved-views";
      error: Error;
      authenticatedSession?: UserSession;
    };

interface AccountLoadFlight {
  attempt: number;
  sessionPromise: Promise<UserSession>;
  promise: Promise<AccountLoadOutcome>;
}

interface FavoriteMutationOperation {
  ticker: string;
  nextFavorite: boolean;
  previousTickers: string[];
}

interface FavoriteMutationFailure {
  message: string;
  operation: FavoriteMutationOperation;
}

type SavedViewMutationOperation =
  | {
      kind: "create";
      optimisticView: SavedViewRecord;
      idempotencyKey: string;
      previousViews: SavedViewRecord[];
      previousSavedViewId: string | null;
    }
  | {
      kind: "update";
      id: string;
      update: SavedViewUpdate;
      optimisticView: SavedViewRecord;
      previousViews: SavedViewRecord[];
      previousSavedViewId: string | null;
    }
  | {
      kind: "delete";
      view: SavedViewRecord;
      previousViews: SavedViewRecord[];
      previousSavedViewId: string | null;
      optimisticState: ScreenerState | null;
    };

interface SavedViewMutationFailure {
  message: string;
  operation: SavedViewMutationOperation;
}

interface UseAccountPersistenceOptions {
  screenerState: ScreenerState;
  setScreenerState: Dispatch<SetStateAction<ScreenerState>>;
}

function normalizedError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function isExpiredAccountError(error: unknown): boolean {
  return (
    error instanceof AccountApiError &&
    (error.httpStatus === 401 || error.httpStatus === 403)
  );
}

function isConvergedSavedViewDelete(error: unknown): boolean {
  return (
    error instanceof AccountApiError &&
    error.httpStatus === 404 &&
    error.serverCode === "saved_view_not_found"
  );
}

async function loadAccountSnapshot(
  sessionPromise: Promise<UserSession>
): Promise<AccountLoadOutcome> {
  let session: UserSession;
  try {
    session = await sessionPromise;
  } catch (error) {
    return { ok: false, kind: "session", error: normalizedError(error) };
  }

  if (!session.authenticated) {
    return {
      ok: true,
      snapshot: { session, favoriteTickers: [], savedViews: [] }
    };
  }

  const [favoritesResult, savedViewsResult] = await Promise.allSettled([
    loadFavoriteTickers(),
    loadSavedViews()
  ]);
  const failures = [favoritesResult, savedViewsResult]
    .filter((result): result is PromiseRejectedResult => result.status === "rejected")
    .map((result) => result.reason as unknown);
  const expired = failures.find(isExpiredAccountError);
  if (expired) {
    return {
      ok: false,
      kind:
        favoritesResult.status === "rejected" && isExpiredAccountError(favoritesResult.reason)
          ? "favorites"
          : "saved-views",
      error: normalizedError(expired),
      authenticatedSession: session
    };
  }
  if (favoritesResult.status === "rejected") {
    return {
      ok: false,
      kind: "favorites",
      error: normalizedError(favoritesResult.reason),
      authenticatedSession: session
    };
  }
  if (savedViewsResult.status === "rejected") {
    return {
      ok: false,
      kind: "saved-views",
      error: normalizedError(savedViewsResult.reason),
      authenticatedSession: session
    };
  }
  return {
    ok: true,
    snapshot: {
      session,
      favoriteTickers: favoritesResult.value,
      savedViews: savedViewsResult.value
    }
  };
}

export function useAccountPersistence({
  screenerState,
  setScreenerState
}: UseAccountPersistenceOptions) {
  const [session, setSession] = useState<UserSession | null>(null);
  const [favoriteTickers, setFavoriteTickers] = useState<string[]>([]);
  const [savedViews, setSavedViews] = useState<SavedViewRecord[]>([]);
  const [favoriteFailure, setFavoriteFailure] = useState<FavoriteMutationFailure | null>(null);
  const [savedViewFailure, setSavedViewFailure] = useState<SavedViewMutationFailure | null>(null);
  const [accountReadState, setAccountReadState] = useState<AccountReadState>({
    availability: "loading"
  });
  const [accountLoadAttempt, setAccountLoadAttempt] = useState(0);
  const [favoriteMutationPending, setFavoriteMutationPending] = useState(false);
  const [savedViewMutationPending, setSavedViewMutationPending] = useState(false);
  const accountGenerationRef = useRef(0);
  const accountLoadFlightRef = useRef<AccountLoadFlight | null>(null);
  const favoriteMutationPendingRef = useRef(false);
  const savedViewMutationPendingRef = useRef(false);
  const optimisticSavedViewIdRef = useRef(0);

  const invalidateAccount = () => {
    accountGenerationRef.current += 1;
    favoriteMutationPendingRef.current = false;
    savedViewMutationPendingRef.current = false;
    setFavoriteMutationPending(false);
    setSavedViewMutationPending(false);
    setSession({ authenticated: false, email: null });
    setFavoriteTickers([]);
    setSavedViews([]);
    setFavoriteFailure(null);
    setSavedViewFailure(null);
    setAccountReadState({ availability: "ready" });
  };

  useEffect(() => {
    const accountGeneration = accountGenerationRef.current + 1;
    accountGenerationRef.current = accountGeneration;
    let isCurrent = true;
    favoriteMutationPendingRef.current = false;
    savedViewMutationPendingRef.current = false;
    setFavoriteMutationPending(false);
    setSavedViewMutationPending(false);
    setAccountReadState({ availability: "loading" });
    let flight = accountLoadFlightRef.current;
    if (flight === null || flight.attempt !== accountLoadAttempt) {
      const sessionPromise = loadSession();
      flight = {
        attempt: accountLoadAttempt,
        sessionPromise,
        promise: loadAccountSnapshot(sessionPromise)
      };
      accountLoadFlightRef.current = flight;
    }
    void flight.sessionPromise.then(
      (nextSession) => {
        if (isCurrent && accountGenerationRef.current === accountGeneration) {
          setSession(nextSession);
        }
      },
      () => undefined
    );
    void flight.promise.then((outcome) => {
      if (!isCurrent || accountGenerationRef.current !== accountGeneration) return;
      if (!outcome.ok) {
        if (isExpiredAccountError(outcome.error)) {
          invalidateAccount();
          return;
        }
        if (outcome.authenticatedSession) setSession(outcome.authenticatedSession);
        setAccountReadState({
          availability: "error",
          kind: outcome.kind,
          error: outcome.error
        });
        return;
      }
      setSession(outcome.snapshot.session);
      setFavoriteTickers(outcome.snapshot.favoriteTickers);
      setSavedViews(outcome.snapshot.savedViews);
      setFavoriteFailure(null);
      setSavedViewFailure(null);
      setAccountReadState({ availability: "ready" });
    });
    return () => {
      isCurrent = false;
    };
  }, [accountLoadAttempt]);

  useEffect(() => {
    if (
      accountReadState.availability !== "ready" ||
      session?.authenticated !== true ||
      screenerState.savedViewId === null ||
      screenerState.savedViewId.startsWith("optimistic-saved-view-") ||
      savedViews.some((view) => view.id === screenerState.savedViewId)
    ) {
      return;
    }
    setScreenerState((current) =>
      current.savedViewId === screenerState.savedViewId
        ? { ...current, savedViewId: null }
        : current
    );
  }, [accountReadState.availability, savedViews, screenerState.savedViewId, session, setScreenerState]);

  const runFavoriteMutation = async (operation: FavoriteMutationOperation) => {
    if (
      !session?.authenticated ||
      accountReadState.availability !== "ready" ||
      favoriteMutationPendingRef.current
    ) {
      return;
    }
    const accountGeneration = accountGenerationRef.current;
    favoriteMutationPendingRef.current = true;
    setFavoriteMutationPending(true);
    setFavoriteFailure(null);
    setFavoriteTickers(
      operation.nextFavorite
        ? Array.from(new Set([...operation.previousTickers, operation.ticker])).sort()
        : operation.previousTickers.filter(
            (favoriteTicker) => favoriteTicker.toUpperCase() !== operation.ticker
          )
    );
    try {
      const nextFavoriteTickers = await saveFavoriteTicker(
        operation.ticker,
        operation.nextFavorite
      );
      if (accountGenerationRef.current !== accountGeneration) return;
      setFavoriteTickers(nextFavoriteTickers);
    } catch (error) {
      if (accountGenerationRef.current !== accountGeneration) return;
      if (isExpiredAccountError(error)) {
        invalidateAccount();
        return;
      }
      setFavoriteTickers(operation.previousTickers);
      setFavoriteFailure({
        message: `Unable to update ${operation.ticker} favorite.`,
        operation
      });
    } finally {
      if (accountGenerationRef.current === accountGeneration) {
        favoriteMutationPendingRef.current = false;
        setFavoriteMutationPending(false);
      }
    }
  };

  const toggleFavoriteTicker = (ticker: string) => {
    const normalizedTicker = ticker.toUpperCase();
    const previousTickers = [...favoriteTickers];
    void runFavoriteMutation({
      ticker: normalizedTicker,
      nextFavorite: !previousTickers.some(
        (favoriteTicker) => favoriteTicker.toUpperCase() === normalizedTicker
      ),
      previousTickers
    });
  };

  const applyOptimisticSavedViewMutation = (operation: SavedViewMutationOperation) => {
    switch (operation.kind) {
      case "create":
        setSavedViews([operation.optimisticView, ...operation.previousViews]);
        setScreenerState((current) => ({
          ...current,
          savedViewId: operation.optimisticView.id
        }));
        break;
      case "update":
        setSavedViews(
          operation.previousViews.map((view) =>
            view.id === operation.id ? operation.optimisticView : view
          )
        );
        break;
      case "delete":
        setSavedViews(operation.previousViews.filter((view) => view.id !== operation.view.id));
        operation.optimisticState = null;
        setScreenerState((current) => {
          if (current.savedViewId !== operation.view.id) return current;
          const optimisticState = { ...current, savedViewId: null };
          operation.optimisticState = optimisticState;
          return optimisticState;
        });
        break;
    }
  };

  const rollbackSavedViewMutation = (operation: SavedViewMutationOperation) => {
    setSavedViews(operation.previousViews);
    setScreenerState((current) => {
      if (operation.kind === "create") {
        return current.savedViewId === operation.optimisticView.id
          ? { ...current, savedViewId: operation.previousSavedViewId }
          : current;
      }
      if (operation.kind === "delete") {
        return operation.optimisticState !== null &&
          current === operation.optimisticState &&
          operation.previousSavedViewId === operation.view.id &&
          current.savedViewId === null
          ? { ...operation.optimisticState, savedViewId: operation.previousSavedViewId }
          : current;
      }
      return current;
    });
  };

  const runSavedViewMutation = async (operation: SavedViewMutationOperation) => {
    if (
      !session?.authenticated ||
      accountReadState.availability !== "ready" ||
      savedViewMutationPendingRef.current
    ) {
      return;
    }
    const accountGeneration = accountGenerationRef.current;
    savedViewMutationPendingRef.current = true;
    setSavedViewMutationPending(true);
    setSavedViewFailure(null);
    applyOptimisticSavedViewMutation(operation);
    try {
      if (operation.kind === "create") {
        const persisted = await createSavedViewRecord(
          operation.optimisticView.name,
          operation.optimisticView.state,
          operation.idempotencyKey
        );
        if (accountGenerationRef.current !== accountGeneration) return;
        setSavedViews((current) =>
          current.map((view) =>
            view.id === operation.optimisticView.id ? persisted : view
          )
        );
        setScreenerState((current) =>
          current.savedViewId === operation.optimisticView.id
            ? { ...current, savedViewId: persisted.id }
            : current
        );
      } else if (operation.kind === "update") {
        const persisted = await updateSavedViewRecord(operation.id, operation.update);
        if (accountGenerationRef.current !== accountGeneration) return;
        setSavedViews((current) =>
          current.map((view) => (view.id === operation.id ? persisted : view))
        );
      } else {
        await deleteSavedViewRecord(operation.view.id);
        if (accountGenerationRef.current !== accountGeneration) return;
      }
    } catch (error) {
      if (accountGenerationRef.current !== accountGeneration) return;
      if (isExpiredAccountError(error)) {
        invalidateAccount();
        return;
      }
      if (operation.kind === "delete" && isConvergedSavedViewDelete(error)) return;
      rollbackSavedViewMutation(operation);
      setSavedViewFailure({
        message:
          operation.kind === "create"
            ? `Unable to save ${operation.optimisticView.name}.`
            : operation.kind === "update"
              ? `Unable to rename ${operation.optimisticView.name}.`
              : `Unable to delete ${operation.view.name}.`,
        operation
      });
    } finally {
      if (accountGenerationRef.current === accountGeneration) {
        savedViewMutationPendingRef.current = false;
        setSavedViewMutationPending(false);
      }
    }
  };

  const createUserSavedView = (name: string) => {
    const normalizedName = normalizeSavedViewName(name);
    if (normalizedName === null) return;
    const timestamp = new Date().toISOString();
    const optimisticView: SavedViewRecord = {
      id: `optimistic-saved-view-${++optimisticSavedViewIdRef.current}`,
      name: normalizedName,
      state: captureSavedViewState(screenerState),
      createdAt: timestamp,
      updatedAt: timestamp
    };
    void runSavedViewMutation({
      kind: "create",
      optimisticView,
      idempotencyKey: crypto.randomUUID(),
      previousViews: [...savedViews],
      previousSavedViewId: screenerState.savedViewId
    });
  };

  const renameUserSavedView = (view: SavedViewRecord, name: string) => {
    const normalizedName = normalizeSavedViewName(name);
    if (normalizedName === null || normalizedName === view.name) return;
    void runSavedViewMutation({
      kind: "update",
      id: view.id,
      update: { name: normalizedName },
      optimisticView: { ...view, name: normalizedName, updatedAt: new Date().toISOString() },
      previousViews: [...savedViews],
      previousSavedViewId: screenerState.savedViewId
    });
  };

  const deleteUserSavedView = (view: SavedViewRecord) => {
    void runSavedViewMutation({
      kind: "delete",
      view,
      previousViews: [...savedViews],
      previousSavedViewId: screenerState.savedViewId,
      optimisticState: null
    });
  };

  const retryAccountData = () => {
    accountGenerationRef.current += 1;
    favoriteMutationPendingRef.current = false;
    savedViewMutationPendingRef.current = false;
    setFavoriteMutationPending(false);
    setSavedViewMutationPending(false);
    setFavoriteFailure(null);
    setSavedViewFailure(null);
    setAccountReadState({ availability: "loading" });
    setAccountLoadAttempt((current) => current + 1);
  };

  return {
    session,
    favoriteTickers,
    savedViews,
    favoriteFailure,
    savedViewFailure,
    accountReadState,
    favoriteMutationPending,
    savedViewMutationPending,
    canUseFavorites: session?.authenticated === true && accountReadState.availability === "ready",
    selectUserSavedView: (view: SavedViewRecord) =>
      setScreenerState((current) => applySavedView(current, view.state, view.id)),
    createUserSavedView,
    renameUserSavedView,
    deleteUserSavedView,
    toggleFavoriteTicker,
    retryAccountData,
    retryFavoriteMutation: () => {
      if (favoriteFailure) void runFavoriteMutation(favoriteFailure.operation);
    },
    retrySavedViewMutation: () => {
      if (savedViewFailure) void runSavedViewMutation(savedViewFailure.operation);
    }
  };
}
